import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Mail } from "lucide-react";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "signin") {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate("/journal", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen flex flex-col" style={{ background: '#141210' }}>
      {/* Nav */}
      <nav className="echo-nav">
        <Link to="/" className="echo-logo" style={{ textDecoration: 'none' }}>ECHO</Link>
      </nav>

      {/* Auth Form */}
      <div className="flex-1 flex items-center justify-center px-6 pt-20">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', color: '#f5f0e8', marginBottom: '0.5rem' }}>
              {mode === "signin" ? "Welcome back" : "Begin your archive"}
            </h1>
            <p style={{ color: '#9a9590', fontSize: '0.9rem' }}>
              {mode === "signin" ? "Your memories are waiting." : "Start remembering your life."}
            </p>
          </div>

          {/* Mode Toggle */}
          <div className="flex mb-8" style={{ background: 'rgba(26,24,20,0.8)', borderRadius: '8px', border: '1px solid #2e2a26', padding: '4px' }}>
            {["signin", "signup"].map(m => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className="flex-1 py-2 text-sm rounded-md transition-all"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  background: mode === m ? 'rgba(196,149,106,0.1)' : 'transparent',
                  color: mode === m ? '#c4956a' : '#6a6560',
                  border: 'none', cursor: 'pointer'
                }}
              >
                {m === "signin" ? "Sign in" : "Sign up"}
              </button>
            ))}
          </div>

          {error && (
            <div data-testid="auth-error" className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.2)', color: '#f87171' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <input
                data-testid="auth-name-input"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={e => setName(e.target.value)}
                className="echo-input"
                required
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="echo-input"
              required
            />
            <input
              data-testid="auth-password-input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="echo-input"
              required
              minLength={6}
            />
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="btn-amber w-full justify-center"
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? "..." : mode === "signin" ? "Sign in" : "Create account"}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div style={{ flex: 1, height: '1px', background: '#2e2a26' }} />
            <span style={{ color: '#6a6560', fontSize: '0.75rem' }}>or</span>
            <div style={{ flex: 1, height: '1px', background: '#2e2a26' }} />
          </div>

          {/* Google Login */}
          <button
            data-testid="google-login-btn"
            onClick={loginWithGoogle}
            className="btn-ghost w-full justify-center"
          >
            <Mail size={16} />
            Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}
